import { useEffect, useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Header } from '../components/layout/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { getAlerts, createAlert, updateAlert, deleteAlert, getUsers, testAlertEmail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, Edit2, AlertTriangle, CheckCircle2, Mail, X, Send, Loader2 } from 'lucide-react';

const PROVIDER_OPTIONS = [
  { value: 'all', label: 'Tous les providers' },
  { value: 'aws', label: 'AWS' },
  { value: 'azure', label: 'Azure' },
  { value: 'gcp', label: 'GCP' },
  { value: 'ovh', label: 'OVH' }
];

export const AlertsPage = () => {
  const { isAdmin } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  const [users, setUsers] = useState([]);
  const [testingEmail, setTestingEmail] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    provider: 'all',
    threshold: '',
    notify_emails: [],
  });

  const fetchAlerts = async () => {
    try {
      const response = await getAlerts();
      setAlerts(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des alertes');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (_) {}
  };

  useEffect(() => {
    fetchAlerts();
    fetchUsers();
  }, []);

  const resetForm = () => {
    setEditingAlert(null);
    setFormData({ name: '', provider: 'all', threshold: '', notify_emails: [] });
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.threshold) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    try {
      if (editingAlert) {
        await updateAlert(editingAlert.id, {
          name: formData.name,
          threshold: parseFloat(formData.threshold),
          notify_emails: formData.notify_emails,
        });
        toast.success('Alerte modifiée');
      } else {
        await createAlert({
          name: formData.name,
          provider: formData.provider,
          threshold: parseFloat(formData.threshold),
          notify_emails: formData.notify_emails,
        });
        toast.success('Alerte créée');
      }
      setDialogOpen(false);
      resetForm();
      fetchAlerts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleEdit = (alert) => {
    setEditingAlert(alert);
    setFormData({
      name: alert.name,
      provider: alert.provider,
      threshold: alert.threshold.toString(),
      notify_emails: alert.notify_emails || [],
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await deleteAlert(id);
      toast.success('Alerte supprimée');
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggle = async (alert) => {
    try {
      await updateAlert(alert.id, { is_active: !alert.is_active });
      fetchAlerts();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleTestEmail = async (alertId) => {
    setTestingEmail(alertId);
    try {
      const response = await testAlertEmail(alertId);
      toast.success(response.data?.message || 'Email de test envoyé');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setTestingEmail(null);
    }
  };

  const toggleEmail = (email) => {
    setFormData(prev => ({
      ...prev,
      notify_emails: prev.notify_emails.includes(email)
        ? prev.notify_emails.filter(e => e !== email)
        : [...prev.notify_emails, email],
    }));
  };

  return (
    <Layout>
      <Header
        title="Alertes Budget"
        subtitle="Configurez des alertes pour être notifié des dépassements de budget"
      />
      {isAdmin && (
        <div className="flex justify-end mb-6">
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-alert-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle alerte
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingAlert ? 'Modifier l\'alerte' : 'Créer une alerte'}</DialogTitle>
                <DialogDescription>
                  Définissez un seuil de coût et les destinataires de la notification.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de l'alerte</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ex: Budget mensuel AWS"
                    data-testid="alert-name-input"
                  />
                </div>
                {!editingAlert && (
                  <div className="space-y-2">
                    <Label htmlFor="provider">Provider</Label>
                    <Select
                      value={formData.provider}
                      onValueChange={(value) => setFormData({...formData, provider: value})}
                    >
                      <SelectTrigger data-testid="alert-provider-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PROVIDER_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label htmlFor="threshold">Seuil (€)</Label>
                  <Input
                    id="threshold"
                    type="number"
                    value={formData.threshold}
                    onChange={(e) => setFormData({...formData, threshold: e.target.value})}
                    placeholder="10000"
                    data-testid="alert-threshold-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>
                    <Mail className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
                    Notifier par email
                  </Label>
                  {users.length === 0 ? (
                    <p className="text-sm text-muted-foreground">Aucun utilisateur disponible.</p>
                  ) : (
                    <div className="space-y-1.5 max-h-40 overflow-y-auto border border-border rounded-lg p-2">
                      {users.map(u => {
                        const selected = formData.notify_emails.includes(u.email);
                        return (
                          <button
                            key={u.email}
                            type="button"
                            onClick={() => toggleEmail(u.email)}
                            className={
                              'flex items-center gap-2 w-full px-2.5 py-2 rounded-md text-sm transition-colors text-left ' +
                              (selected
                                ? 'bg-primary/10 text-primary'
                                : 'hover:bg-accent text-foreground')
                            }
                          >
                            <div className={
                              'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ' +
                              (selected ? 'bg-primary border-primary' : 'border-border')
                            }>
                              {selected && (
                                <svg viewBox="0 0 12 12" className="w-3 h-3 text-primary-foreground">
                                  <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <span className="font-medium">{u.name}</span>
                              <span className="text-muted-foreground ml-2 text-xs truncate">{u.email}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {formData.notify_emails.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {formData.notify_emails.map(email => (
                        <Badge key={email} variant="secondary" className="gap-1 pr-1">
                          <span className="text-xs truncate max-w-[150px]">{email}</span>
                          <button
                            type="button"
                            onClick={() => toggleEmail(email)}
                            className="hover:bg-muted rounded p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={handleSubmit} data-testid="alert-submit-btn">
                  {editingAlert ? 'Modifier' : 'Créer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      <Card data-testid="alerts-table-card">
        <CardHeader>
          <CardTitle className="text-lg font-[Outfit]">Liste des alertes</CardTitle>
          <CardDescription>
            {alerts.length} alerte(s) configurée(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Aucune alerte configurée</p>
              {isAdmin && (
                <p className="text-sm text-muted-foreground mt-2">
                  Créez votre première alerte pour surveiller vos coûts.
                </p>
              )}
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden sm:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Provider</TableHead>
                      <TableHead>Seuil</TableHead>
                      <TableHead>Notifications</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actif</TableHead>
                      {isAdmin && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {alerts.map(alert => (
                      <TableRow key={alert.id} data-testid={`alert-row-${alert.id}`}>
                        <TableCell className="font-medium">{alert.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {PROVIDER_OPTIONS.find(p => p.value === alert.provider)?.label || alert.provider}
                          </Badge>
                        </TableCell>
                        <TableCell>{alert.threshold.toLocaleString('fr-FR')} €</TableCell>
                        <TableCell>
                          {(alert.notify_emails || []).length > 0 ? (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
                              <span className="text-sm text-muted-foreground">
                                {alert.notify_emails.length} dest.
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {alert.is_triggered ? (
                            <Badge variant="destructive">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Déclenché
                            </Badge>
                          ) : (
                            <Badge variant="secondary">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Normal
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={alert.is_active}
                            onCheckedChange={() => isAdmin && handleToggle(alert)}
                            disabled={!isAdmin}
                            data-testid={`alert-toggle-${alert.id}`}
                          />
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {(alert.notify_emails || []).length > 0 && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleTestEmail(alert.id)}
                                disabled={testingEmail === alert.id}
                                title="Envoyer un email de test"
                              >
                                {testingEmail === alert.id
                                  ? <Loader2 className="w-4 h-4 animate-spin" />
                                  : <Send className="w-4 h-4" />}
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(alert)}
                              data-testid={`edit-alert-${alert.id}`}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(alert.id)}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-alert-${alert.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div className="sm:hidden space-y-3">
                {alerts.map(alert => (
                  <div
                    key={alert.id}
                    data-testid={`alert-row-${alert.id}`}
                    className="border border-border rounded-lg p-4 space-y-3"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-medium">{alert.name}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <Badge variant="outline" className="text-xs">
                            {PROVIDER_OPTIONS.find(p => p.value === alert.provider)?.label || alert.provider}
                          </Badge>
                          {alert.is_triggered ? (
                            <Badge variant="destructive" className="text-xs">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Déclenché
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Normal
                            </Badge>
                          )}
                        </div>
                        {(alert.notify_emails || []).length > 0 && (
                          <div className="flex items-center gap-1 mt-1.5">
                            <Mail className="w-3 h-3 text-muted-foreground" />
                            <span className="text-xs text-muted-foreground">
                              {alert.notify_emails.join(', ')}
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="text-lg font-semibold whitespace-nowrap">
                        {alert.threshold.toLocaleString('fr-FR')} €
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={alert.is_active}
                          onCheckedChange={() => isAdmin && handleToggle(alert)}
                          disabled={!isAdmin}
                          data-testid={`alert-toggle-${alert.id}`}
                        />
                        <span className="text-sm text-muted-foreground">
                          {alert.is_active ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          {(alert.notify_emails || []).length > 0 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleTestEmail(alert.id)}
                              disabled={testingEmail === alert.id}
                            >
                              {testingEmail === alert.id
                                ? <Loader2 className="w-4 h-4 animate-spin" />
                                : <Send className="w-4 h-4" />}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(alert)}
                            data-testid={`edit-alert-${alert.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(alert.id)}
                            className="text-destructive hover:text-destructive"
                            data-testid={`delete-alert-${alert.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
};
